import { createContext, FC, useEffect, useState } from 'react';

import {
  countries as fetchCountries,
  Country,
  currencies as fetchCurrencies,
  Currency,
  CurrentIp,
  currentIp as fetchCurrentIp,
  playerFields as fetchPlayerFields,
  PlayerFields,
} from '../../api/common';

import { camelize } from '../../helpers/common';

export interface CommonContextData {
  playerFields: PlayerFields | undefined;
  withCaptcha: boolean;
  currencies: Currency[];
  countries: Country[];

  currentIp: CurrentIp | undefined;
}

interface CommonContextActions {}

export const CommonContext = createContext<
  CommonContextData & CommonContextActions
>(null as any);

const initialState = {
  playerFields: undefined,
  withCaptcha: false,
  currencies: [],
  countries: [],
  userGroups: [],

  currentIp: undefined,
};

interface CommonContextProviderProps {
  notAllowedCountries?: { list: string[]; component: JSX.Element };
}

export const CommonContextProvider: FC<CommonContextProviderProps> = ({
  notAllowedCountries,
  children,
}) => {
  const [blockService, setBlockService] = useState<boolean>();

  const [state, setState] = useState<CommonContextData>(initialState);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: currentIp } = await fetchCurrentIp()
          .then(({ data }) => ({ data: JSON.parse(data) }))
          .then(({ data }) => ({ data: camelize(data) as CurrentIp }));

        if (
          notAllowedCountries != null &&
          notAllowedCountries.list.includes(currentIp.countryCode)
        ) {
          setBlockService(true);

          return;
        }

        setBlockService(false);

        const [
          { data: playerFields },
          { data: currencies },
          { data: countries },
        ] = await Promise.all([
          fetchPlayerFields(),
          fetchCurrencies(),
          fetchCountries(),
        ]);

        setState((s) => ({
          ...s,
          playerFields,
          currencies,
          countries,
          currentIp,
        }));
      } catch {
        setState(initialState);
      }
    };

    fetchData();
  }, [notAllowedCountries]);

  if (blockService == null) return null;

  if (blockService)
    return <>{notAllowedCountries != null && notAllowedCountries.component}</>;

  return (
    <CommonContext.Provider value={state}>{children}</CommonContext.Provider>
  );
};
